import {
  Badge,
  Button,
  Empty,
  Form,
  Popconfirm,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { serverUrl } from "../../../../../nestserver";
import { uploadDoc } from "../../../../../services/upload.service";
import ComposeMessageModal from "./modals/ComposeMessageModal";

const { Text } = Typography;

const AnnouncementSchedules = () => {
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ── Core compose state (used only for editing a schedule) ──
  const [classId, setClassId] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedParents, setSelectedParents] = useState([]);
  const [subject, setSubject] = useState("");
  const [messageText, setMessageText] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [composeModalVisible, setComposeModalVisible] = useState(false);
  const [targetingType, setTargetingType] = useState("selected");

  const [sendType, setSendType] = useState("now");
  const [scheduledDate, setScheduledDate] = useState(null);
  const [recurrenceConfig, setRecurrenceConfig] = useState({
    type: "daily",
    at_time: "09:00",
    days_of_week: [1, 2, 3, 4, 5],
    timezone: "Asia/Kolkata",
  });

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeSchedules, setActiveSchedules] = useState([]);

  const recurringCount = activeSchedules.filter((s) => s.isRecurring).length;
  const oneTimeCount = activeSchedules.filter((s) => !s.isRecurring).length;

  // ────────────────────────────────────────────────
  // Data fetching
  // ────────────────────────────────────────────────
  const fetchClasses = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classes = useQuery("classes", fetchClasses);

  const fetchStudentData = async () => {
    if (!classId) return [];
    const res = await axios.get(serverUrl + "/students/class/" + classId, {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classDetails = useQuery(["class", classId], fetchStudentData, {
    enabled: !!classId,
  });

  const fetchMeta = async () => {
    try {
      const res = await axios.get(
        serverUrl + "/personal-messages/announcement-meta",
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      const result = res.data?.result;
      if (result) {
        setTemplates(result.templates || []);
        setActiveSchedules(result.schedules || []);
      }
    } catch (err) {
      console.error("Failed to fetch announcement meta", err);
    }
  };

  useEffect(() => {
    fetchMeta();
  }, []);

  // ────────────────────────────────────────────────
  // Schedule handlers
  // ────────────────────────────────────────────────
  const handleToggleSchedule = async (id, currentStatus) => {
    try {
      await axios.patch(
        serverUrl + `/message-schedules/${id}/status`,
        { is_active: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      message.success(
        `Schedule ${!currentStatus ? "started" : "stopped"} successfully`,
      );
      fetchMeta();
    } catch (err) {
      console.error(err);
      message.error("Failed to update schedule status");
    }
  };

  const handleDeleteSchedule = async (id, isRecurring = true) => {
    try {
      const endpoint = isRecurring
        ? `/message-schedules/${id}`
        : `/personal-messages/${id}`;
      await axios.delete(serverUrl + endpoint, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      message.success(
        isRecurring ? "Schedule deleted" : "Scheduled message deleted",
      );
      fetchMeta();
    } catch (err) {
      message.error("Failed to delete schedule");
    }
  };

  // ────────────────────────────────────────────────
  // Edit schedule → open compose modal pre-filled
  // ────────────────────────────────────────────────
  const resetForm = () => {
    setSubject("");
    setMessageText("");
    setUploadedFiles([]);
    setSelectedStudents([]);
    setSelectedParents([]);
    setClassId(null);
    setEditingMessageId(null);
    setSendType("now");
    setScheduledDate(null);
    setSelectedTemplate(null);
    form.resetFields();
  };

  const handleEditSchedule = (schedule) => {
    resetForm();
    setEditingMessageId(schedule._id);
    setSubject(schedule.subject);
    setMessageText(schedule.message || schedule.content);
    setUploadedFiles(schedule.media || []);
    setSendType(schedule.isRecurring ? "recurring" : "scheduled");
    setSelectedTemplate(schedule.template_id?._id || schedule.template_id);

    if (schedule.isRecurring) {
      setRecurrenceConfig({
        ...(schedule.recurrence || {}),
        timezone: schedule.timezone || "Asia/Kolkata",
      });
      setTargetingType(schedule.recipient_type);

      if (schedule.class_ids && schedule.class_ids.length > 0) {
        setClassId(schedule.class_ids[0]);
      } else if (
        schedule.recipient_type === "class" &&
        schedule.recipient_ids?.length > 0
      ) {
        setClassId(schedule.recipient_ids[0]);
      }

      if (
        schedule.recipient_type === "selected" &&
        schedule.recipient_ids?.length > 0
      ) {
        const parents = schedule.recipient_ids.map((u) => {
          const details = u.parent || u.teacher || u.student || u;
          return {
            ...(typeof u === "object" ? u : { _id: u }),
            first_name: details.first_name || u.first_name || "",
            last_name: details.last_name || u.last_name || "",
            role: u.role || "Parent",
          };
        });
        setSelectedParents(parents);
      }
    } else {
      setScheduledDate(moment(schedule.scheduled_at));
      if (schedule.class_ids && schedule.class_ids.length > 0) {
        setClassId(schedule.class_ids[0]);
      }
      if (schedule.recipient_ids && schedule.recipient_ids.length > 0) {
        const parents = schedule.recipient_ids.map((u) => {
          const details = u.parent || u.teacher || u.student || u;
          return {
            ...(typeof u === "object" ? u : { _id: u }),
            first_name: details.first_name || u.first_name || "",
            last_name: details.last_name || u.last_name || "",
            role: u.role || "Parent",
          };
        });
        setSelectedParents(parents);
      }
    }

    setComposeModalVisible(true);
  };

  // ── Reconciliation for Edit Mode ──────────────────
  useEffect(() => {
    if (classDetails?.data?.length > 0 && selectedParents?.length > 0) {
      const parentIds = new Set(selectedParents.map((p) => p._id));
      const filteredStudents = classDetails.data.filter(
        (s) => s.parent && parentIds.has(s.parent._id),
      );

      if (selectedStudents.length === 0 && filteredStudents.length > 0) {
        setSelectedStudents(filteredStudents);
      }
    }
  }, [classDetails.data, selectedParents, selectedStudents.length]);

  const handleClassChange = (value) => {
    setClassId(value);
    setSelectedStudents([]);
  };

  const handleTargetingTypeChange = (value) => {
    setTargetingType(value);
    if (value === "all") {
      setClassId(null);
      setSelectedStudents([]);
      setSelectedParents([]);
    }
  };

  const handleSelectAllStudents = () => {
    if (!classDetails?.data) return;
    const studentsWithParents = classDetails.data.filter((s) => s.parent);
    const studentsWithoutParents = classDetails.data.filter((s) => !s.parent);

    if (studentsWithoutParents.length > 0) {
      message.warning(
        `${studentsWithoutParents.length} student(s) skipped (no parent assigned)`,
      );
    }

    setSelectedStudents(studentsWithParents);

    const uniqueParents = [];
    const parentIds = new Set();
    studentsWithParents.forEach((student) => {
      if (!parentIds.has(student.parent._id)) {
        parentIds.add(student.parent._id);
        uniqueParents.push({
          ...student.parent,
          student_name: `${student.first_name} ${student.last_name}`,
          role: student.parent.role || "Parent",
        });
      }
    });
    setSelectedParents(uniqueParents);
  };

  const handleStudentMultiChange = (values) => {
    const newStudents = values.map((id) =>
      classDetails?.data?.find((s) => s._id === id),
    );
    setSelectedStudents(newStudents);

    const uniqueParents = [];
    const parentIds = new Set();
    newStudents.forEach((student) => {
      if (student?.parent && !parentIds.has(student.parent._id)) {
        parentIds.add(student.parent._id);
        uniqueParents.push({
          ...student.parent,
          student_name: `${student.first_name} ${student.last_name}`,
          role: student.parent.role || "Parent",
        });
      }
    });
    setSelectedParents(uniqueParents);
  };

  const removeParent = (parentId) => {
    setSelectedParents(selectedParents.filter((p) => p._id !== parentId));
    setSelectedStudents(
      selectedStudents.filter((s) => s.parent._id !== parentId),
    );
  };

  // ────────────────────────────────────────────────
  // File handling
  // ────────────────────────────────────────────────
  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (!files || files?.length === 0) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "audio/mpeg",
      "audio/mp3",
      "video/mp4",
      "audio/wav",
      "audio/webm",
    ];

    const maxTotalFiles = 15;
    const responses = [];

    try {
      for (const file of files) {
        if (!allowedTypes.includes(file.type)) {
          message.error(
            `Invalid file type for ${file.name}. Please upload PDF, JPG, PNG, MP4, MP3, WAV, or WebM files only.`,
          );
          continue;
        }

        let folder = "images";
        if (file.type === "application/pdf") {
          folder = "pdf";
        } else if (file.type.startsWith("video/")) {
          folder = "video";
        } else if (file.type.startsWith("audio/")) {
          folder = "audio";
        }

        const res = await uploadDoc({ file }, folder, (progress) => {
          // message.loading(`Uploading ${file.name}: ${progress}%`, 0.5);
        });

        const audioFile = Array.from(files).find(
          (f) => f.name === res?.files?.name,
        );
        const isVoice =
          audioFile?.type === "audio/wav" || audioFile?.type === "audio/webm";
        const mediaType =
          file.type === "application/pdf"
            ? "PDF"
            : file.type.startsWith("video/")
              ? "VIDEO"
              : file.type.startsWith("audio/")
                ? isVoice
                  ? "VOICE"
                  : "AUDIO"
                : "IMAGE";

        responses.push({ ...res, mediaType });
      }

      message.success("Files uploaded successfully");
      setUploadedFiles((prevDocs) => {
        const newDocs = responses.reduce((acc, item) => {
          if (item?.files?.name && item?.id) {
            const mediaItem = { name: item?.files?.name, id: item?.id };
            if (item?.mediaType) mediaItem.type = item?.mediaType;
            acc.push(mediaItem);
          }
          return acc;
        }, []);
        const updatedDocs = Array.isArray(prevDocs)
          ? [...prevDocs, ...newDocs]
          : newDocs;
        return updatedDocs.slice(0, maxTotalFiles);
      });
    } catch (error) {
      console.error(error);
      message.error("File upload failed");
    }
  };

  const onRemoveFile = (index) => {
    setUploadedFiles((old) => old.filter((_, i) => i !== index));
    message.success("File removed");
  };

  const handleSendTypeChange = (e) => setSendType(e.target.value);

  // ────────────────────────────────────────────────
  // Save edits (always an update — this screen never creates new)
  // ────────────────────────────────────────────────
  const handleSendMessage = async () => {
    if (isSending) return;

    if (!subject.trim()) {
      message.error("Please enter a subject");
      return;
    }
    if (!messageText.trim()) {
      message.error("Please enter a message");
      return;
    }
    if (targetingType === "selected" && selectedParents.length === 0) {
      message.error("Please select at least one student/parent");
      return;
    }

    const payload = {
      subject,
      message: messageText,
      recipient_ids:
        targetingType === "all" ? [] : selectedParents.map((p) => p._id),
      class_ids: targetingType === "all" ? [] : (classId ? [classId] : []),
      media: uploadedFiles?.length > 0 ? uploadedFiles : [],
      template_id: selectedTemplate,
      send_to_all: targetingType === "all",
    };

    if (sendType === "scheduled" && scheduledDate) {
      payload.scheduled_at = scheduledDate.toISOString();
    }

    setIsSending(true);
    try {
      if (sendType === "recurring") {
        const recurringPayload = {
          ...payload,
          recipient_type: targetingType,
          template_id: selectedTemplate || undefined,
          recurrence: recurrenceConfig,
          timezone: recurrenceConfig.timezone,
        };
        await axios.patch(
          serverUrl + `/message-schedules/${editingMessageId}`,
          recurringPayload,
          {
            headers: {
              Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
            },
          },
        );
        message.success("Schedule updated successfully");
      } else {
        await axios.patch(
          serverUrl + "/personal-messages/" + editingMessageId,
          payload,
          {
            headers: {
              Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
            },
          },
        );
        message.success("Scheduled message updated successfully");
      }

      resetForm();
      setComposeModalVisible(false);
      fetchMeta();
    } catch (err) {
      console.error(err);
      message.error("Failed to update message");
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (uploadedFiles?.length > 0 && inputRef.current) {
      inputRef.current.focus();
    }
  }, [uploadedFiles]);

  return (
    <div className="mt-8">
      <div
        style={{ borderRadius: "10px", color: "#616161", overflowY: "auto" }}
        className="bg-white drop-shadow-md p-8 w-full screen-height"
      >
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-xl font-bold m-0 text-gray-800">
            Active Schedules
          </h2>
          {activeSchedules.length > 0 && (
            <Badge
              count={activeSchedules.length}
              style={{ backgroundColor: "#1677ff" }}
            />
          )}
        </div>

        {activeSchedules.length > 0 && (
          <Space style={{ marginBottom: 16 }}>
            {recurringCount > 0 && (
              <Tag color="blue">{recurringCount} Recurring</Tag>
            )}
            {oneTimeCount > 0 && <Tag color="green">{oneTimeCount} One-time</Tag>}
          </Space>
        )}

        <div style={{ maxWidth: 720 }}>
          {activeSchedules.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary">No active schedules found.</Text>
              }
            />
          ) : (
            activeSchedules.map((s) => (
              <div
                key={s._id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  padding: "14px 16px",
                  border: "1px solid #f0f0f0",
                  borderLeft: s.isRecurring
                    ? "4px solid #1677ff"
                    : "4px solid #52c41a",
                  borderRadius: 8,
                  marginBottom: 10,
                  background: "#fafafa",
                  gap: 12,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ display: "block", marginBottom: 4 }}>
                    {s.subject}
                  </Text>

                  {s.isRecurring ? (
                    <Space size={4} style={{ marginBottom: 4 }}>
                      <Tag color="blue" style={{ margin: 0 }}>
                        RECURRING
                      </Tag>
                      <Text style={{ fontSize: 12 }}>
                        {s.recurrence?.type?.toUpperCase()} at{" "}
                        {s.recurrence?.at_time} ({s.timezone || "Asia/Kolkata"})
                      </Text>
                    </Space>
                  ) : (
                    <Space size={4} style={{ marginBottom: 4 }}>
                      <Tag color="green" style={{ margin: 0 }}>
                        ONE-TIME
                      </Tag>
                      <Text style={{ fontSize: 12 }}>
                        Scheduled for{" "}
                        {s.scheduled_at
                          ? new Date(s.scheduled_at).toLocaleString()
                          : "—"}
                      </Text>
                    </Space>
                  )}

                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block" }}
                  >
                    Recipients: {s.recipient_ids?.length || 0} parent
                    {s.recipient_ids?.length !== 1 ? "s" : ""}
                    {s.class_ids?.length > 0 &&
                      ` · ${s.class_ids.length} class${
                        s.class_ids.length > 1 ? "es" : ""
                      }`}
                  </Text>

                  {s.isRecurring && (
                    <div style={{ marginTop: 8 }}>
                      <Tag color={s.is_active ? "success" : "default"}>
                        {s.is_active ? "ACTIVE" : "INACTIVE"}
                      </Tag>
                    </div>
                  )}

                  {s.media?.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Space size={4} align="center">
                        <span
                          title="Attachments"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: 12,
                          }}
                        >
                          📎
                        </span>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {s.media.length} attachment
                          {s.media.length !== 1 ? "s" : ""}
                        </Text>
                      </Space>
                    </div>
                  )}
                </div>

                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <Button
                    size="small"
                    onClick={() => handleEditSchedule(s)}
                    style={{
                      borderColor: "#1677ff",
                      color: "#1677ff",
                    }}
                  >
                    Edit
                  </Button>
                  {s.isRecurring && (
                    <Button
                      size="small"
                      type={s.is_active ? "default" : "primary"}
                      onClick={() => handleToggleSchedule(s._id, s.is_active)}
                      style={{
                        background: s.is_active ? undefined : "#52c41a",
                      }}
                    >
                      {s.is_active ? "Stop" : "Start"}
                    </Button>
                  )}
                  <Popconfirm
                    title={
                      s.isRecurring
                        ? "Delete this recurring schedule?"
                        : "Remove this scheduled message?"
                    }
                    description="This action cannot be undone."
                    onConfirm={() => handleDeleteSchedule(s._id, s.isRecurring)}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                  >
                    <Button
                      size="small"
                      danger
                      style={{ flexShrink: 0 }}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ComposeMessageModal
        visible={composeModalVisible}
        onClose={() => {
          setComposeModalVisible(false);
          resetForm();
        }}
        form={form}
        inputRef={inputRef}
        isEditMode
        classes={classes?.data || []}
        classId={classId}
        onClassChange={handleClassChange}
        classDetails={classDetails}
        selectedStudents={selectedStudents}
        selectedParents={selectedParents}
        onSelectAll={handleSelectAllStudents}
        onRemoveParent={removeParent}
        onStudentMultiChange={handleStudentMultiChange}
        subject={subject}
        onSubjectChange={(e) => setSubject(e.target.value)}
        messageText={messageText}
        onMessageChange={(e) => setMessageText(e.target.value)}
        uploadedFiles={uploadedFiles}
        onRemoveFile={onRemoveFile}
        onFileSelect={handleFileSelect}
        sendType={sendType}
        onSendTypeChange={handleSendTypeChange}
        scheduledDate={scheduledDate}
        onScheduledDate={(val) => setScheduledDate(val)}
        recurrenceConfig={recurrenceConfig}
        onRecurrenceChange={(partial) =>
          setRecurrenceConfig((prev) => ({ ...prev, ...partial }))
        }
        templates={templates}
        selectedTemplate={selectedTemplate}
        onTemplateChange={(val, template) => {
          if (template) {
            setSubject(template.subject);
            setMessageText(template.content);
          }
          setSelectedTemplate(val);
        }}
        onInsertTag={(tag) =>
          setMessageText((prev) => prev + (prev ? " " : "") + tag)
        }
        onSend={handleSendMessage}
        onResetForm={resetForm}
        targetingType={targetingType}
        onTargetingTypeChange={handleTargetingTypeChange}
        isSending={isSending}
      />
    </div>
  );
};

export default AnnouncementSchedules;
